import { PixelCanvas, TEAM_RECOLOR } from '../lib';
import type { PaletteKey } from '../palette';
import {
  bodyFrames,
  DEAD_BODY,
  finish,
  HAT_Y,
  hatFrames,
  type Kit,
  LEGS,
  type Part,
  type Recolor,
  recolored,
  recoloredPart,
  selOutline,
  stack,
} from './body';
import { HAT_PALETTES } from './hats';
import { HEADWEAR, type Headwear, SUN_HAT } from './headwear';
import { BEARDS, DEAD_FACE, EXTRAS, EYES, FACE, HAIR_COLOURS, HAIRS, MOUTHS, SKINS, turned } from './looks';
import { OUTFITS, type Outfit } from './outfits';
import { SET_ART, type SetArt } from './sets';

export { HAT_PALETTES } from './hats';
export { SET_ART } from './sets';

/**
 * The look options drawn here (ADR-0073, ADR-0088, ADR-0090), keyed like content `LOOKS` and in its order (assets may
 * not import content: keep them in sync; a client test checks). The first option of each slot is the default look (the
 * beard's and the extras' `none`, the overalls).
 */
export const GARDENER_LOOKS = {
  skin: ['peach', 'fair', 'ivory', 'tan', 'bronze', 'brown', 'umber', 'deep'],
  hair: [
    'bob',
    'short',
    'pigtails',
    'long',
    'bun',
    'curly',
    'afro',
    'braids',
    'locs',
    'buzz',
    'topknot',
    'puffs',
    'sleek',
  ],
  hairColour: ['chestnut', 'black', 'blonde', 'ginger', 'silver', 'pink'],
  eyes: ['round', 'lashes', 'sleepy', 'happy', 'monolid', 'almond'],
  mouth: ['smile', 'grin', 'cat', 'tongue'],
  beard: ['none', 'stubble', 'moustache', 'pencil', 'handlebar', 'goatee', 'full'],
  extras: ['none', 'shades', 'glasses', 'earring', 'chain'],
  outfit: [
    'overalls',
    'hoodie',
    'shirt',
    'gnome',
    'beekeeper',
    'astronaut',
    'pumpkin',
    'reaper',
    'creator',
    'inferno',
    'kitsune',
    'voidwalker',
    'tempest',
    'glacier',
    'angler',
    'jester',
    'plague',
    'chronomancer',
    'glitch',
    'disco',
    'origami',
    'ooze',
    'barnstormer',
    'pirate',
    'magician',
    'chef',
    'knight',
    'sheriff',
    'luchador',
  ],
} as const satisfies {
  skin: readonly (keyof typeof SKINS)[];
  hair: readonly (keyof typeof HAIRS)[];
  hairColour: readonly (keyof typeof HAIR_COLOURS)[];
  eyes: readonly (keyof typeof EYES)[];
  mouth: readonly (keyof typeof MOUTHS)[];
  beard: readonly (keyof typeof BEARDS)[];
  extras: readonly (keyof typeof EXTRAS)[];
  outfit: readonly OutfitKey[];
};

export type GardenerLook = { [S in keyof typeof GARDENER_LOOKS]: (typeof GARDENER_LOOKS)[S][number] };
export type GardenerHat = keyof typeof HAT_PALETTES;
export type GardenerTeam = 'blue' | 'red';

export const DEFAULT_GARDENER_LOOK: GardenerLook = {
  skin: 'peach',
  hair: 'bob',
  hairColour: 'chestnut',
  eyes: 'round',
  mouth: 'smile',
  beard: 'none',
  extras: 'none',
  outfit: 'overalls',
};

/**
 * The sprites' shape (the atlas's `gardener` and `gardener_hat` are drawn from it, in the default look). `portrait`: the
 * square [x, y, size] of the body's idle frame 0 an avatar shows (hat and face down to the collar, the face centred; high
 * enough for a cone's slopes and a turban's dome, ADR-0089).
 * `hand`: the fist on a held gun's grip, `origin` its top-left (a gun sprite's `hand` point) inside its outline.
 */
export const GARDENER = {
  body: { size: [64, 72], origin: [32, 71], density: 4, fps: { idle: 2.5, run: 12, dead: 0 }, portrait: [14, 8, 42] },
  hat: { size: [64, 40], origin: [32, 24], density: 4, fps: { fly: 14, rest: 0 } },
  hand: { size: [18, 14], origin: [2, 2], density: 4 },
} as const;

export type GardenerFrames = Record<'idle' | 'run' | 'dead' | 'fly' | 'rest' | 'hand', PixelCanvas[]>;

/**
 * The fist on a gun's grip (14×10 art px): the thumb along the top with a lit ridge and a rounded tip, a crease under
 * it, four fingers wrapped round the grip with their knuckle creases, lit from the top-left; in the look's skin, so every
 * gun is held in your own hand (ADR-0073).
 */
const HAND = PixelCanvas.fromGrid(`
  ..ssssssssss..
  .sssssssssssss
  .ssssssssssSSS
  ssSSSSSSSSSSS.
  ssssssssssssss
  sssssssssssssS
  sssSsssSsssSS.
  sssSsssSsssS..
  .SSSSSSSSSSS..
  ...SSSSSSS....
`);

/** The fist in `skin`, outlined like the body, on a canvas with room for its outline (`GARDENER.hand`). */
function handFrame(skin: Recolor): PixelCanvas {
  const [w, h] = GARDENER.hand.size;
  const c = new PixelCanvas(w, h).blit(recolored(HAND, skin), ...GARDENER.hand.origin);
  selOutline(c);
  return c;
}

/** Every outfit's key: the everyday ones (outfits.ts) and the sets' suits (ADR-0112). */
type OutfitKey = keyof typeof OUTFITS | keyof typeof SET_ART;

/** Outfit `key`'s art: a set's suit is drawn the first time it is worn (ADR-0217, `drawnLater`). */
const outfitArt = (key: OutfitKey): Outfit =>
  (OUTFITS as Partial<Record<OutfitKey, Outfit>>)[key] ?? SET_ART[key as keyof typeof SET_ART].outfit;

/** `hat`'s own art (ADR-0089, ADR-0112; a set's drawn the first time it is worn), else none: the sun hat in its palette. */
const headArt = (hat: GardenerHat): Headwear | undefined =>
  (HEADWEAR as Partial<Record<GardenerHat, Headwear>>)[hat] ??
  (SET_ART as Partial<Record<GardenerHat, SetArt>>)[hat]?.head;

/** Nothing on the head's hat layer (headwear tied on is drawn in the hair's place). */
const NONE = new PixelCanvas(1, 1);
const NO_HAT = { flat: NONE, up: NONE, down: NONE };

/** Each mouth turned with the dead pose's head. */
const DEAD_MOUTHS = Object.fromEntries(Object.entries(MOUTHS).map(([k, part]) => [k, turned(part)])) as Record<
  keyof typeof MOUTHS,
  Part
>;

/** Paints a part onto a fresh canvas the size of `base`'s part, at `base`'s position. */
function over(base: Part, ...parts: Part[]): Part {
  const [b, bx, by] = base;
  const c = b.clone();
  for (const [p, x, y] of parts) c.blit(p, x - bx, y - by);
  return [c, bx, by];
}

/**
 * The face under a fringe: the skin just below hair takes its shadow (`S`), and the hair's pixels that border skin
 * below or beside them take its deep tone (`N`), so any hair colour parts from any skin.
 */
function shadeUnder(face: Part, hair: Part): [Part, Part] {
  const [f, fx, fy] = face;
  const [h, hx, hy] = hair;
  const skin = f.clone();
  const lined = h.clone();
  const hairAt = (x: number, y: number) => h.get(x - hx, y - hy) !== null;
  const skinAt = (x: number, y: number) => !hairAt(x, y) && f.get(x - fx, y - fy) !== null;
  for (let y = 0; y < f.h; y++) {
    for (let x = 0; x < f.w; x++) {
      if (f.get(x, y) === 's' && hairAt(x + fx, y + fy - 1) && !hairAt(x + fx, y + fy)) skin.set(x, y, 'S');
    }
  }
  for (let y = 0; y < h.h; y++) {
    for (let x = 0; x < h.w; x++) {
      if (h.get(x, y) === null) continue;
      const [sx, sy] = [x + hx, y + hy];
      if (skinAt(sx, sy + 1) || skinAt(sx - 1, sy) || skinAt(sx + 1, sy)) lined.set(x, y, 'N');
    }
  }
  return [
    [skin, fx, fy],
    [lined, hx, hy],
  ];
}

/** A look's parts under `hat` on `team`, recoloured (ADR-0073, ADR-0088): what `drawGardener` and `drawWardrobe` draw. */
function dress(look: GardenerLook, hat: GardenerHat, team: GardenerTeam) {
  const teamMap: Recolor = team === 'blue' ? {} : TEAM_RECOLOR[team];
  const hatMap: Recolor = { ...HAT_PALETTES[hat], ...teamMap };
  const skin: Recolor = SKINS[look.skin];
  const hairColour: Recolor = HAIR_COLOURS[look.hairColour];
  const style = HAIRS[look.hair];
  const eyes = EYES[look.eyes];
  const mouth = MOUTHS[look.mouth];
  const beard = BEARDS[look.beard];
  const extra = EXTRAS[look.extras];
  const outfit: Outfit = outfitArt(look.outfit);
  // The outfit in the team's colours, an open collar in the look's skin (outfits.ts).
  const dressed: Recolor = { ...skin, ...teamMap };
  const hairy = (parts: readonly Part[]) => parts.map((p) => recoloredPart(p, hairColour));
  const suited = (part: Part) => recoloredPart(recoloredPart(part, outfit.legs ?? {}), teamMap);
  // Headwear (ADR-0089): a hat over the hair, or headwear tied on in the hair's place (it covers the head).
  const wear: Headwear = headArt(hat) ?? SUN_HAT;
  const onHead = wear.worn ? NO_HAT : wear.hat;

  const [face, hair] = shadeUnder(FACE, wear.worn ? wear.front : style.front);
  const [deadFace, deadHair] = shadeUnder(DEAD_FACE, wear.worn ? wear.dead : style.dead);
  const head = wear.worn ? recoloredPart(wear.front, hatMap) : recoloredPart(hair, hairColour);
  const deadHead = wear.worn ? recoloredPart(wear.dead, hatMap) : recoloredPart(deadHair, hairColour);
  const kit: Kit = {
    hat: {
      flat: recolored(onHead.flat, hatMap),
      up: recolored(onHead.up, hatMap),
      down: recolored(onHead.down, hatMap),
    },
    // Before its palette, in the team's colours: a hat in the team's colour (a set's) takes the team's rim.
    hatKey: {
      flat: recolored(onHead.flat, teamMap),
      up: recolored(onHead.up, teamMap),
      down: recolored(onHead.down, teamMap),
    },
    worn: wear.worn,
    ribbon: Object.fromEntries(
      Object.entries(wear.ribbon).map(([k, part]) => [k, recoloredPart(part, teamMap)]),
    ) as Kit['ribbon'],
    // A suit's trousers and boots, then the team's (outfits.ts).
    legs: Object.fromEntries(Object.entries(LEGS).map(([k, part]) => [k, suited(part)])),
    torso: stack(recoloredPart(outfit.torso, dressed), ...extra.torso),
    behind: wear.worn ? [] : hairy(style.back),
    face: recoloredPart(over(face, eyes.open, mouth), skin),
    blink: recoloredPart(over(face, eyes.blink, mouth), skin),
    // The beard under the hair (a lock falls over it), the glasses and the earring over both.
    hair: stack(
      ...hairy(beard.front),
      head,
      ...(wear.worn ? [] : wear.head.map((p) => recoloredPart(p, hatMap))),
      ...(wear.worn && wear.mask ? [] : extra.face),
    ),
    dead: [
      suited(DEAD_BODY),
      recoloredPart(outfit.dead, dressed),
      recoloredPart(over(deadFace, eyes.dead, DEAD_MOUTHS[look.mouth]), skin),
      stack(
        ...hairy(beard.dead),
        deadHead,
        ...(wear.worn ? [] : (wear.lying ?? []).map((p) => recoloredPart(p, hatMap))),
        ...(wear.worn && wear.mask ? [] : extra.dead),
      ),
    ],
  };
  return { kit, wear, hatMap, teamMap, skin };
}

/**
 * A gardener's frames for `look`, wearing `hat`, on `team` (ADR-0073, ADR-0088): the body's (idle 6f, run 6f, dead 1f,
 * 64×72) and the hat's on its own (fly 4f, rest 1f, 64×40), in final colours and outlined. Drawn at runtime by the client
 * for every look it meets (well under 10 ms a call); the atlas has the default one.
 */
export function drawGardener(look: GardenerLook, hat: GardenerHat, team: GardenerTeam): GardenerFrames {
  const { kit, wear, teamMap, skin } = dress(look, hat, team);
  const flies = wear.worn ? { fly: [], rest: [] } : wear;
  return { ...bodyFrames(kit), ...hatFrames(flies, HAT_PALETTES[hat], teamMap), hand: [handFrame(skin)] };
}

/**
 * The top of the head a hat always covers, for the wardrobe's wig: a dome of hair over the face's flat top (the
 * hairstyles are drawn from the brim down), lit on the left like the hair; recoloured like it.
 */
const CROWN: Part = (() => {
  const [cx, cy, rx, ry] = [30, 27, 19.5, 11];
  const c = new PixelCanvas(44, 14);
  const [x0, y0] = [8, 13];
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      const [dx, dy] = [(x + x0 + 0.5 - cx) / rx, (y + y0 + 0.5 - cy) / ry];
      const r = dx * dx + dy * dy;
      if (r > 1) continue;
      // Sheen up and to the left, shadow down the right side, the mid tone between.
      c.set(x, y, r > 0.8 && dx > 0.3 ? 'N' : -dx - dy > 0.95 ? 'S' : dx - dy * 0.3 > 0.45 ? 'n' : 'm');
    }
  }
  return [c, x0, y0];
})();

/** The row under a part's lowest pixel (frame px). */
function foot([c, , y]: Part): number {
  for (let row = c.h - 1; row >= 0; row--) for (let x = 0; x < c.w; x++) if (c.get(x, row) !== null) return y + row + 1;
  return y;
}

/** The hair behind the wig: a knot that sat on the hat's crown sits on the wig's (`CROWN`). */
const bare = (behind: readonly Part[]): Part[] =>
  behind.map(([c, x, y]) => [c, x, y + Math.max(0, CROWN[2] + 3 - foot([c, x, y]))] as Part);

/** A colour option's swatch (the gardener screen, ADR-0091): its tone and its shadow, as the skin or the hair takes them. */
export function lookSwatch(slot: 'skin' | 'hairColour', option: string): readonly PaletteKey[] {
  const map: Recolor =
    slot === 'skin' ? SKINS[option as keyof typeof SKINS] : HAIR_COLOURS[option as keyof typeof HAIR_COLOURS];
  const keys: readonly PaletteKey[] = slot === 'skin' ? ['s', 'S'] : ['m', 'n'];
  return keys.map((k) => map[k] ?? k);
}

/**
 * What a wardrobe picture shows (the gardener screen's tiles, ADR-0091): one thing a gardener wears, alone, or `head`
 * (ADR-0112): the gardener standing, cut at the collar (the avatar's `portrait` rows).
 */
export type WardrobePart = 'head' | 'hat' | 'clothes' | 'hair' | 'eyes' | 'mouth' | 'beard' | 'extras';

/**
 * Only `part` of a gardener standing, on nobody (where idle frame 0 has it on the body's 64×72 frame): `hat` the hat
 * with what it puts on the head (a chin strap, a veil) or the headwear tied on; `clothes` the top and the trousers;
 * `hair` the hairstyle as a wig (its crown, which a hat always covers, made up: `CROWN`); `eyes`, `mouth`, `beard` and
 * `extras` alone, in the look's skin and hair colour. What stands out of a figure (a hat, clothes, a wig) is outlined
 * like it; what sits on the face is not.
 */
export function drawWardrobe(
  look: GardenerLook,
  hat: GardenerHat,
  team: GardenerTeam,
  part: WardrobePart,
): PixelCanvas {
  const { kit, wear, hatMap, teamMap, skin } = dress(look, hat, team);
  const hairColour: Recolor = HAIR_COLOURS[look.hairColour];
  const c = new PixelCanvas(...GARDENER.body.size);
  const put = ([p, x, y]: Part, dy = 0) => c.blit(p, x, y + dy);
  if (part === 'head') {
    const body = bodyFrames(kit).idle[0] as PixelCanvas;
    const [, top, size] = GARDENER.body.portrait;
    for (let y = 0; y < top + size; y++) for (let x = 0; x < c.w; x++) c.set(x, y, body.get(x, y));
    return c;
  }
  if (part === 'hat') {
    if (wear.worn) put(recoloredPart(wear.alone ?? wear.front, hatMap));
    else for (const p of wear.head) put(recoloredPart(p, hatMap));
    put(kit.ribbon.hang, HAT_Y);
    c.blit(kit.hat.flat, 0, HAT_Y);
    finish(c, new PixelCanvas(c.w, c.h).blit(kit.hatKey.flat, 0, HAT_Y));
    return c;
  }
  if (part === 'clothes') {
    put(kit.legs.stand as Part);
    put(recoloredPart(outfitArt(look.outfit).torso, { ...skin, ...teamMap }));
  } else if (part === 'hair') {
    const style = HAIRS[look.hair];
    for (const p of bare(style.back)) put(recoloredPart(p, hairColour));
    put(recoloredPart(CROWN, hairColour));
    put(recoloredPart(style.front, hairColour));
  } else {
    const extra = EXTRAS[look.extras];
    const own: Record<typeof part, readonly Part[]> = {
      eyes: [recoloredPart(EYES[look.eyes].open, skin)],
      mouth: [recoloredPart(MOUTHS[look.mouth], skin)],
      beard: BEARDS[look.beard].front.map((p) => recoloredPart(p, hairColour)),
      extras: [...extra.torso, ...extra.face],
    };
    for (const p of own[part]) put(p);
    return c;
  }
  finish(c);
  return c;
}
